import { callFirebaseFunction } from '../../../../services/firebaseFunctions';
import { refreshUsersByOrgId } from '../../../../services/user';

export interface MergeEmployeesRequest {
  orgId: string;
  /** The two candidates — order does not matter to the backend. */
  userAId: string;
  userBId: string;
  /** The profile the admin opened the merge from. */
  initiatedFromUserId: string;
  /** Chosen value per clashing field, keyed by dotted Firestore path. */
  resolvedFields: Record<string, unknown>;
}

export interface MergeEmployeesResponse {
  success: boolean;
  /** The SURVIVING record — may NOT be the profile the merge started on. */
  keptUserId: string;
  deletedUserId: string;
  migrated: {
    experiences: number;
    moments: number;
    enrollmentClaims: number;
    pinnedItems: number;
  };
}

export const mergeEmployees = (req: MergeEmployeesRequest) =>
  callFirebaseFunction<MergeEmployeesResponse>('user_mergeEmployees', req).then(
    (res) => {
      // One record is deleted and the survivor updated — the org user set is a
      // one-shot fetch, so refresh it (else pickers still offer the deleted user).
      refreshUsersByOrgId(req.orgId);
      return res;
    }
  );

/** The exact copy the product wants when a merge collides with a running import. */
export const IMPORT_IN_PROGRESS_MESSAGE =
  'Merging users is not possible when an import is running. Please try again in a few minutes.';

type FirebaseCallableError = {
  code?: string;
  message?: string;
  details?: { code?: string };
};

/**
 * Map a rejected `user_mergeEmployees` call to admin-facing copy. Branches on
 * the HttpsError `code` and the nested `details.code` the backend attaches.
 */
export const getMergeErrorMessage = (err: unknown): string => {
  const e = (err || {}) as FirebaseCallableError;
  const code = e.code;
  const detailCode = e.details?.code;

  if (detailCode === 'MERGE_IMPORT_IN_PROGRESS') {
    return IMPORT_IN_PROGRESS_MESSAGE;
  }
  if (detailCode === 'MERGE_SAME_USER') {
    return 'You cannot merge an employee with themselves.';
  }
  if (detailCode === 'MERGE_CROSS_ORG') {
    return 'These employees belong to different organizations and cannot be merged.';
  }
  if (detailCode === 'MERGE_USER_NOT_FOUND') {
    return 'One of the employees could not be found. Please refresh and try again.';
  }
  if (detailCode === 'MERGE_INVALID_ARGS') {
    return 'Something went wrong with the merge request. Please refresh and try again.';
  }

  switch (code) {
    case 'unauthenticated':
      return 'Your session has expired. Please sign in again.';
    case 'permission-denied':
      return 'You do not have permission to merge employees in this organization.';
    case 'invalid-argument':
      return 'Something went wrong with the merge request. Please refresh and try again.';
    case 'not-found':
      return 'One of the employees could not be found. Please refresh and try again.';
    case 'failed-precondition':
      return IMPORT_IN_PROGRESS_MESSAGE;
    default:
      return e.message || 'Could not merge employees. Please try again.';
  }
};
